'use client';

import { useMemo, useState } from 'react';
import { ArrowUpDown, Check, ChevronDown, Filter, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import type {
  BusinessDirectoryFacets,
  BusinessDirectoryFilterOption,
  BusinessDirectoryFiltersState,
  BusinessDirectorySort,
} from '@/types';

interface BusinessDirectoryFiltersProps {
  facets?: BusinessDirectoryFacets;
  initialFacets?: BusinessDirectoryFacets;
  filters: BusinessDirectoryFiltersState;
  sort: BusinessDirectorySort;
  isLoading?: boolean;
  onChange: (filters: BusinessDirectoryFiltersState) => void;
  onSortChange: (sort: BusinessDirectorySort) => void;
}

type FilterKey = keyof BusinessDirectoryFiltersState;

const FILTER_LABELS: Record<FilterKey, string> = {
  category: 'Category',
  city: 'City',
  state: 'State',
  country: 'Country',
};

function facetKey(key: FilterKey): keyof BusinessDirectoryFacets {
  if (key === 'category') return 'categories';
  if (key === 'city') return 'cities';
  if (key === 'state') return 'states';
  return 'countries';
}

function sortOptionsAlphabetically(options: BusinessDirectoryFilterOption[]) {
  return [...options].sort((a, b) =>
    a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
  );
}

const SORT_OPTIONS: { value: BusinessDirectorySort; label: string }[] = [
  { value: 'business', label: 'Business name' },
  { value: 'name', label: 'Name' },
];

function SortDropdown({
  value,
  onSelect,
}: {
  value: BusinessDirectorySort;
  onSelect: (value: BusinessDirectorySort) => void;
}) {
  const [open, setOpen] = useState(false);
  const selected = SORT_OPTIONS.find((option) => option.value === value) ?? SORT_OPTIONS[0];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 gap-1.5 rounded-lg font-medium shrink-0"
          aria-label="Sort directory"
        >
          <ArrowUpDown className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">{selected.label}</span>
          <span className="sm:hidden">Sort</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-48 p-0" align="end">
        <Command>
          <CommandList>
            <CommandGroup>
              {SORT_OPTIONS.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.label}
                  onSelect={() => {
                    onSelect(option.value);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value === option.value ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  {option.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function FilterDropdown({
  label,
  options,
  value,
  isLoading,
  onSelect,
}: {
  label: string;
  options: BusinessDirectoryFilterOption[];
  value?: string | null;
  isLoading?: boolean;
  onSelect: (value: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const sortedOptions = useMemo(() => sortOptionsAlphabetically(options), [options]);
  const displayValue = value || 'All';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant={value ? 'default' : 'outline'}
          size="sm"
          className={cn(
            'h-9 w-full justify-between gap-1.5 rounded-lg font-medium',
            value && 'shadow-sm'
          )}
        >
          <span className="truncate">{displayValue}</span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-70" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-0">
        <Command>
          <CommandInput placeholder={`Search ${label.toLowerCase()}...`} />
          <CommandList>
            <CommandEmpty>No {label.toLowerCase()} found.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="__all__"
                onSelect={() => {
                  onSelect(null);
                  setOpen(false);
                }}
                className={cn(!value && 'font-medium')}
              >
                <Check className={cn('mr-2 h-4 w-4', !value ? 'opacity-100' : 'opacity-0')} />
                All
              </CommandItem>
              {isLoading && options.length === 0 ? (
                <CommandItem disabled value="__loading__">
                  Loading options...
                </CommandItem>
              ) : (
                sortedOptions.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    onSelect={() => {
                      onSelect(option.value === value ? null : option.value);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        value === option.value ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    <span className="flex-1 truncate">{option.label}</span>
                    <Badge variant="secondary" className="ml-2 shrink-0 tabular-nums">
                      {option.count}
                    </Badge>
                  </CommandItem>
                ))
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function BusinessDirectoryFiltersBar({
  facets,
  initialFacets,
  filters,
  sort,
  isLoading,
  onChange,
  onSortChange,
}: BusinessDirectoryFiltersProps) {
  const activeCount = useMemo(
    () => [filters.category, filters.city, filters.state, filters.country].filter(Boolean).length,
    [filters]
  );

  const updateFilter = (key: FilterKey, value: string | null) => {
    onChange({ ...filters, [key]: value || null });
  };

  const clearAll = () => {
    onChange({
      category: null,
      city: null,
      state: null,
      country: null,
    });
  };

  const filterKeys = useMemo<FilterKey[]>(() => ['country', 'state', 'city', 'category'], []);
  const [isMobileOpen, setIsMobileOpen] = useState(activeCount > 0);

  const persistentlyHiddenKeys = useMemo(() => {
    if (!initialFacets) return new Set<FilterKey>();
    const hidden = new Set<FilterKey>();
    filterKeys.forEach((key) => {
      const options = initialFacets[facetKey(key)] ?? [];
      if (options.length <= 1) hidden.add(key);
    });
    return hidden;
  }, [filterKeys, initialFacets]);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground"
          onClick={() => setIsMobileOpen((open) => !open)}
        >
          <Filter className="h-4 w-4" />
          <span>Filters</span>
          <ChevronDown
            className={cn(
              'h-4 w-4 transition-transform sm:hidden',
              isMobileOpen ? 'rotate-180' : 'rotate-0'
            )}
          />
        </button>
        <div className="flex items-center gap-1.5 shrink-0">
          <SortDropdown value={sort} onSelect={onSortChange} />
          {activeCount > 0 && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 rounded-lg text-muted-foreground"
              onClick={clearAll}
            >
              Clear all
            </Button>
          )}
        </div>
      </div>

      <div
        className={cn(
          'overflow-hidden transition-[max-height,opacity] duration-200 ease-out',
          'sm:max-h-none sm:opacity-100',
          isMobileOpen || activeCount > 0 ? 'max-h-[420px] opacity-100' : 'max-h-0 opacity-0'
        )}
      >
        <div className="grid grid-cols-2 gap-3 pt-1 sm:grid-cols-4 sm:pt-0">
          {filterKeys.map((key) => {
            const options = facets?.[facetKey(key)] ?? [];
            if (persistentlyHiddenKeys.has(key)) return null;

            return (
              <div key={key} className="flex min-w-0 flex-col gap-1.5">
                <Label className="text-xs font-medium text-muted-foreground">
                  {FILTER_LABELS[key]}
                </Label>
                <FilterDropdown
                  label={FILTER_LABELS[key]}
                  options={options}
                  value={filters[key]}
                  isLoading={isLoading}
                  onSelect={(value) => updateFilter(key, value)}
                />
              </div>
            );
          })}
        </div>
      </div>

      {activeCount > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {filterKeys.map((key) => {
            const value = filters[key];
            if (!value) return null;
            return (
              <Badge
                key={key}
                variant="secondary"
                className="gap-1 pl-2.5 pr-1 py-1 rounded-full font-medium"
              >
                <span className="text-muted-foreground">{FILTER_LABELS[key]}:</span>
                {value}
                <button
                  type="button"
                  className="ml-0.5 rounded-full p-0.5 hover:bg-background/80"
                  onClick={() => updateFilter(key, null)}
                  aria-label={`Remove ${FILTER_LABELS[key]} filter`}
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            );
          })}
        </div>
      )}
    </div>
  );
}
